import React, { useCallback, useState, useEffect, useContext } from "react";
import {
  MenuItem,
  AppBar,
  Grid,
  Tabs,
  Tab,
  Toolbar,
  FormControl,
  Paper,
  InputBase,
  Select,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import withStyles from "@mui/styles/withStyles";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { compose } from "recompose";
import WarningModal from "../WarningModal/WarningModal";
import "./nav.scss";
import { httpGet } from "../../hooks/http";
import { UserContext } from "../../UserContext";
import useMediaQuery from "@mui/material/useMediaQuery";

// FIXME checkout https://mui.com/components/use-media-query/#migrating-from-withwidth
const withWidth = () => (WrappedComponent) => (props) =>
  <WrappedComponent {...props} width="xs" />;

const styles = {
  margin: {
    padding: 10,
  },
  space: {
    margin: "20px 10px 20px 10px",
  },
  indicator: {
    backgroundColor: "transparent",
  },
  transform: {
    textTransform: "none",
    fontWeight: "bold",
  },
};

const SelectInput = withStyles((theme) => ({
  input: {
    borderRadius: 4,
    position: "relative",
    backgroundColor: theme.palette.background.paper,
    border: "1px solid #ced4da",
    fontSize: 16,
    padding: "10px 17px 10px 12px",
    transition: theme.transitions.create(["border-color", "box-shadow"]),
    "&:focus": {
      borderRadius: 4,
      borderColor: "#80bdff",
      boxShadow: "0 0 0 0.2rem rgba(0,123,255,.25)",
    },
  },
}))(InputBase);

const Nav = (props) => {
  const { eventCount, settings, access_token, canViewComments, contact } =
    useContext(UserContext);
  const { classes, width } = props;
  const [dropdown, setDropdown] = useState(
    sessionStorage.getItem("menu") ? sessionStorage.getItem("menu") : 0
  );
  const [warning, setWarning] = useState(false);
  const [currentState, setCurrentState] = useState();

  const useIsWidthDown = (breakpoint, width) => {
    const theme = useTheme();
    return useMediaQuery(theme.breakpoints.down(breakpoint));
  };

  const [menu, setMenu] = useState([
    {
      nameSingular: "Current Event",
      namePlural: "Current Events",
      prefix: "current",
      value: 0,
      count: eventCount.current,
      pathname: "/",
      display: settings.navOptions.showCurrentEvents,
    },
    {
      nameSingular: "Past Event",
      namePlural: "Past Events",
      prefix: "past",
      value: 1,
      count: eventCount.past,
      display: settings.navOptions.showPastEvents,
      pathname: "/past",
    },
    {
      nameSingular: "Approval",
      namePlural: "Approvals",
      prefix: "approval",
      value: 2,
      count: eventCount.approval,
      display: settings.navOptions.showEventApprovals,
      pathname: "/approve",
    },
    {
      nameSingular: "Payment",
      namePlural: "Payments",
      prefix: "payment",
      count: eventCount.payment,
      display: settings.navOptions.showOnlinePayments,
      value: 3,
      pathname: "/payment",
    },
  ]);

  const getEventCount = useCallback(() => {
    httpGet(
      `clientPortal/clients/${contact.id}/eventCounts`,
      access_token
    ).then((result) => {
      const { response, loaded } = result;
      if (loaded) {
        menu.forEach(
          (element) => (element.count = response.eventCount[element.prefix])
        );
        setMenu(menu);
      }
    });
  }, [access_token, contact.id, menu]);
  useEffect(() => {
    let current = {};
    if (sessionStorage.getItem("menu")) {
      current = menu.find((element) => {
        return element.value === parseInt(sessionStorage.getItem("menu"));
      });
      setCurrentState(current);
    }

    let interval = setInterval(() => {
      getEventCount();
    }, 60000);
    return () => {
      clearInterval(interval);
    };
  }, [getEventCount, menu]);

  const close = (save) => {
    if (save) {
      let current = menu.find((elem) => {
        return elem.pathname === sessionStorage.getItem("redirect");
      });
      sessionStorage.removeItem("showWarning");
      sessionStorage.setItem("menu", current.value);
      setDropdown(current.value);
    }
    setWarning(false);
  };

  const isExtraSmall = useIsWidthDown("xs", width);

  const click = (event, value) => {
    if (Boolean(sessionStorage.getItem("showWarning")) === true) {
      setWarning(true);
      if (isExtraSmall)
        sessionStorage.setItem("redirect", menu[event.target.value].pathname);
      else sessionStorage.setItem("redirect", menu[value].pathname);
      event.preventDefault();
      return false;
    }

    if (isExtraSmall) {
      sessionStorage.setItem("menu", event.target.value);
      setDropdown(event.target.value);
    } else {
      sessionStorage.setItem("menu", menu[value].value);
      setCurrentState(menu[value]);
    }

    getEventCount();
    sessionStorage.removeItem("showOne");
  };

  return !canViewComments ? (
    !isExtraSmall ? (
      <Paper className={classes.space}>
        <WarningModal close={close} show={warning} />
        <Tabs
          value={currentState?.value || 0}
          classes={{
            indicator: classes.indicator,
          }}
          className="hoverEffect"
          onChange={click}
          variant="fullWidth"
        >
          {menu.map((element, index) => {
            return element.display ? (
              <Tab
                key={index}
                component={Link}
                value={element.value}
                className={classes.transform}
                to={element.pathname}
                label={`${
                  element.count > 1 ? element.namePlural : element.nameSingular
                }`}
              />
            ) : null;
          })}
        </Tabs>
      </Paper>
    ) : (
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        className={classes.margin}
      >
        <WarningModal close={close} show={warning} />

        <AppBar position="static" color="default">
          <Toolbar>
            <Grid item xs={6}>
              <Typography
                variant="subtitle1"
                color="inherit"
                className={classes.bold}
              >
                Select One
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth={true} className={classes.margin}>
                <Select
                  value={dropdown}
                  onChange={(event) => click(event, "")}
                  input={<SelectInput name="age" id="age-customized-select" />}
                >
                  {menu.map((element, index) => {
                    return element.display ? (
                      <MenuItem
                        key={index}
                        component={Link}
                        to={element.pathname}
                        value={element.value}
                      >
                        {element.count > 1
                          ? element.namePlural
                          : element.nameSingular}{" "}
                        ({element.count})
                      </MenuItem>
                    ) : null;
                  })}
                </Select>
              </FormControl>
            </Grid>
          </Toolbar>
        </AppBar>
      </Grid>
    )
  ) : null;
};

Nav.propTypes = {
  classes: PropTypes.object.isRequired,
  width: PropTypes.string.isRequired,
};
export default compose(withStyles(styles), withWidth())(Nav);
